// AI Chat Widget - Main Container
// Opens in a dialog, manages conversation state, integrates with Zustand store

import { useState, useEffect, useMemo } from 'react';
import { MessageCircle, X, Plus, ExternalLink, ChevronDown, Sparkles, Building2, BookOpen, FileText, PanelRightClose } from 'lucide-react';
import { Rnd } from 'react-rnd';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useChatStore, useActiveConversation, useActiveMessages } from '@/stores/chatStore';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ConversationList } from './ConversationList';
import { SourcesPanel } from './SourcesPanel';
import { AgentBanner } from './AgentBanner';
import { createComponentLogger } from '@/utils/logger';
import { AGENT_CONFIGS, DEFAULT_AGENT, getAgentConfig } from '@/config/agents';
import type { AgentType, CitationMetadata } from '@/types/chat';

const log = createComponentLogger('ChatWidget', 'ai-chat');

// Icon mapping for agent selector
const AGENT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Sparkles,
  Building2,
  BookOpen
};

// ============================================================================
// Component Props
// ============================================================================

interface ChatWidgetProps {
  defaultAgentType?: AgentType;
  className?: string;
  onClose?: () => void;
  /** Controlled mode: external open state */
  isOpen?: boolean;
  /** Controlled mode: callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Hide the floating button (use when controlled externally) */
  hideButton?: boolean;
}

// ============================================================================
// Main Component
// ============================================================================

export function ChatWidget({
  defaultAgentType = DEFAULT_AGENT,
  className = '',
  onClose,
  isOpen: controlledIsOpen,
  onOpenChange,
  hideButton = false
}: ChatWidgetProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const setIsOpen = (open: boolean) => {
    if (isControlled && onOpenChange) {
      onOpenChange(open);
    } else {
      setInternalIsOpen(open);
    }
  };
  const [showConversationList, setShowConversationList] = useState(false);
  const [selectedAgentType, setSelectedAgentType] = useState<AgentType>(defaultAgentType);
  const [showSourcesPanel, setShowSourcesPanel] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState<CitationMetadata | null>(null);

  // Resizable dialog state
  const [size, setSize] = useState({
    width: 1024,
    height: typeof window !== 'undefined' ? window.innerHeight * 0.85 : 600
  });
  const [position, setPosition] = useState({
    x: typeof window !== 'undefined' ? (window.innerWidth - 1024) / 2 : 0,
    y: typeof window !== 'undefined' ? (window.innerHeight * 0.075) : 0
  });

  // Zustand store hooks
  const {
    conversations,
    activeConversationId,
    isLoadingConversations,
    isSending,
    error,
    loadConversations,
    createConversation,
    setActiveConversation,
    sendMessageStreaming,
    regenerateMessage,
    clearError
  } = useChatStore();

  const activeConversation = useActiveConversation();
  const messages = useActiveMessages();

  // Filter conversations by selected agent type
  const filteredConversations = conversations.filter(
    (c) => c.agent_type === selectedAgentType
  );

  // ============================================================================
  // Effects
  // ============================================================================

  // Load conversations when widget opens
  useEffect(() => {
    if (isOpen && conversations.length === 0) {
      log.info('Loading conversations on widget open');
      loadConversations();
    }
  }, [isOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) {
return;
}

    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to close (only in floating mode, not when controlled externally)
      if (e.key === 'Escape' && !isControlled) {
        e.preventDefault();
        handleClose();
        log.debug('Closed widget via Escape key');
      }

      // Ctrl+Shift+S to toggle sources panel
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        if (allCitations.length > 0) {
          setShowSourcesPanel((prev) => !prev);
          log.debug('Toggled sources panel via keyboard shortcut');
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isControlled, allCitations.length]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleOpen = () => {
    log.info('Opening chat widget');
    setIsOpen(true);
  };

  const handleClose = () => {
    log.info('Closing chat widget');
    setIsOpen(false);
    clearError();
    if (onClose) {
onClose();
}
  };

  const handleNewConversation = async (agentType?: AgentType) => {
    const typeToUse = agentType || selectedAgentType;
    log.info('Creating new conversation', { agentType: typeToUse });
    try {
      const conversationId = await createConversation(typeToUse);
      setActiveConversation(conversationId);
      setShowConversationList(false);
      log.info('New conversation created', { conversationId });
    } catch (error) {
      log.error('Failed to create conversation', { agentType: typeToUse }, error as Error);
    }
  };

  const handleSendMessage = async (content: string, images?: File[]) => {
    let targetConversationId = activeConversationId;

    if (!targetConversationId) {
      log.warn('Attempted to send message without active conversation');
      // Auto-create conversation if none exists
      targetConversationId = await createConversation(selectedAgentType);
      setActiveConversation(targetConversationId);
    }

    log.info('Sending message', {
      conversationId: targetConversationId,
      contentLength: content.length,
      imageCount: images?.length || 0
    });

    // Pass conversationId directly to avoid state timing issues
    await sendMessageStreaming(content, images, targetConversationId);
  };

  const handleSelectConversation = (conversationId: string) => {
    log.debug('Selecting conversation', { conversationId });
    setActiveConversation(conversationId);
    setShowConversationList(false);
  };

  const handlePopout = () => {
    log.info('Opening chat in popout window');

    // Build URL with current conversation if exists
    const url = activeConversationId
      ? `/chat-popout?conversation=${activeConversationId}`
      : '/chat-popout';

    // Open in a new window with specific dimensions
    const width = 500;
    const height = 700;
    const left = window.screenX + window.outerWidth - width - 50;
    const top = window.screenY + 50;

    const popoutWindow = window.open(
      url,
      'lendwise-chat',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (popoutWindow) {
      // Close the inline widget
      handleClose();
    }
  };

  const handleSelectCitation = (citation: CitationMetadata) => {
    log.debug('Citation selected', { documentId: citation.document_id, title: citation.title });
    setSelectedCitation(citation);
    setShowSourcesPanel(true);
  };

  const handleCloseSourcesPanel = () => {
    setShowSourcesPanel(false);
    setSelectedCitation(null);
  };

  // Collect all citations from current messages for the sources panel
  const allCitations = useMemo(() => {
    const citations: CitationMetadata[] = [];
    for (const msg of messages) {
      if (msg.metadata?.citations) {
        citations.push(...msg.metadata.citations);
      }
    }
    return citations;
  }, [messages]);

  // ============================================================================
  // Render
  // ============================================================================

  const agentConfig = activeConversation
    ? getAgentConfig(activeConversation.agent_type)
    : getAgentConfig(selectedAgentType);

  // Get the icon component for the current agent
  const AgentIcon = AGENT_ICONS[agentConfig.icon] || Sparkles;

  return (
    <>
      {/* Floating Action Button - hidden when controlled externally */}
      {!hideButton && (
        <Button
          onClick={handleOpen}
          size="lg"
          className={`fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg ${className}`}
          aria-label="Open AI Chat"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {/* Note: Overlay removed to prevent blocking dropdown menus and other UI elements */}

      {/* Resizable Chat Dialog */}
      {isOpen && (
        <Rnd
          size={size}
          position={position}
          onDragStop={(e, d) => setPosition({ x: d.x, y: d.y })}
          onResizeStop={(e, direction, ref, delta, newPosition) => {
            setSize({
              width: ref.offsetWidth,
              height: ref.offsetHeight
            });
            setPosition(newPosition);
          }}
          minWidth={600}
          minHeight={400}
          maxWidth={typeof window !== 'undefined' ? window.innerWidth * 0.95 : 1920}
          maxHeight={typeof window !== 'undefined' ? window.innerHeight * 0.95 : 1080}
          bounds="window"
          dragHandleClassName="drag-handle"
          style={{ zIndex: 50, position: 'fixed' }}
          enableResizing={{
            top: true,
            right: true,
            bottom: true,
            left: true,
            topRight: true,
            bottomRight: true,
            bottomLeft: true,
            topLeft: true
          }}
        >
          <div className="w-full h-full flex flex-col bg-background/95 backdrop-blur-xl border border-border/50 shadow-2xl sm:rounded-lg overflow-hidden">
          {/* Header - Compact */}
          <div className="drag-handle px-4 py-2 border-b border-border flex flex-row items-center justify-between shrink-0 cursor-move">
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 gap-2 px-2">
                    <AgentIcon className="h-4 w-4" />
                    <span className="font-semibold">{agentConfig.name}</span>
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {(Object.keys(AGENT_CONFIGS) as AgentType[])
                    .filter((key) => ['wisr', 'encompass_helper', 'investor_guidelines'].includes(key))
                    .map((key) => {
                      const config = AGENT_CONFIGS[key];
                      const Icon = AGENT_ICONS[config.icon] || Sparkles;
                      return (
                        <DropdownMenuItem
                          key={key}
                          onClick={() => {
                            setSelectedAgentType(key);
                            // Clear active conversation to show welcome screen for new agent
                            setActiveConversation(null);
                          }}
                          className="gap-2"
                        >
                          <Icon className="h-4 w-4" />
                          <div className="flex flex-col">
                            <span>{config.name}</span>
                            <span className="text-xs text-muted-foreground">{config.description}</span>
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex items-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setShowConversationList(!showConversationList)}
                      disabled={isLoadingConversations}
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{showConversationList ? 'Hide' : 'Show'} History</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleNewConversation()}
                      disabled={isSending}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>New conversation</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {allCitations.length > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={showSourcesPanel ? 'secondary' : 'ghost'}
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setShowSourcesPanel(!showSourcesPanel)}
                      >
                        <FileText className="h-4 w-4" />
                        {allCitations.length > 0 && (
                          <Badge
                            variant="secondary"
                            className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
                          >
                            {allCitations.length}
                          </Badge>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{showSourcesPanel ? 'Hide' : 'Show'} Sources</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handlePopout}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Pop out to window</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleClose}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Close</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex flex-1 overflow-hidden">
            {/* Conversation List Sidebar - Collapsible */}
            {showConversationList && (
              <div className="w-60 border-r border-border bg-muted/5 flex flex-col shrink-0">
                <div className="px-3 py-2 border-b border-border/50 flex items-center justify-between">
                  <h3 className="font-medium text-xs text-muted-foreground uppercase tracking-wide">History</h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleNewConversation()}
                    disabled={isSending}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                <ScrollArea className="flex-1">
                  <ConversationList
                    conversations={filteredConversations}
                    activeConversationId={activeConversationId}
                    onSelect={handleSelectConversation}
                    onNew={handleNewConversation}
                  />
                </ScrollArea>
              </div>
            )}

            {/* Chat Area with Sources Panel */}
            <PanelGroup direction="horizontal" className="flex-1">
              {/* Main Chat Panel */}
              <Panel defaultSize={showSourcesPanel ? 55 : 100} minSize={40}>
                <div className="flex-1 flex flex-col h-full">
                  {/* Error Display */}
                  {error && (
                    <div className="px-6 py-3 bg-destructive/10 backdrop-blur-sm border-b border-destructive/20 animate-in slide-in-from-top-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-destructive">{error.message}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearError}
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Agent Banner - Shows active agent context */}
                  {activeConversation && (
                    <AgentBanner agentType={activeConversation.agent_type} />
                  )}

                  {/* Messages Area */}
                  <ScrollArea className="flex-1 px-6">
                    {activeConversationId ? (
                      <MessageList
                        conversationId={activeConversationId}
                        messages={messages}
                        isLoading={isSending}
                        onSelectCitation={handleSelectCitation}
                        onRegenerate={regenerateMessage}
                        allowRegenerate={activeConversation?.agent_type !== 'wisr'}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center p-8">
                        <AgentIcon className="h-16 w-16 mb-4 text-primary" />
                        <h3 className="text-xl font-semibold mb-2">
                          {agentConfig.name}
                        </h3>
                        <p className="text-muted-foreground mb-6 max-w-md">
                          {agentConfig.description}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Type a message to start a conversation...
                        </p>
                      </div>
                    )}
                  </ScrollArea>

                  {/* Input Area */}
                  <div className="border-t border-border p-3">
                    <MessageInput
                      onSend={handleSendMessage}
                      disabled={isSending}
                      placeholder={
                        activeConversationId
                          ? `Ask ${agentConfig.name}...`
                          : 'Start a new conversation...'
                      }
                      autoFocus={isOpen}
                    />
                  </div>
                </div>
              </Panel>

              {/* Sources Panel - Resizable */}
              {showSourcesPanel && allCitations.length > 0 && (
                <>
                  <PanelResizeHandle className="w-1.5 bg-border hover:bg-primary/20 transition-colors cursor-col-resize" />
                  <Panel defaultSize={45} minSize={25} maxSize={60}>
                    <SourcesPanel
                      citations={allCitations}
                      selectedCitation={selectedCitation}
                      onSelectCitation={handleSelectCitation}
                      onClose={handleCloseSourcesPanel}
                    />
                  </Panel>
                </>
              )}
            </PanelGroup>
          </div>
          </div>
        </Rnd>
      )}
    </>
  );
}
